import type {
  ButtonProps,
  DialogBodyProps,
  DialogContentProps,
  TextProps,
} from '@chakra-ui/react';
import { Flex, Box, Portal as PortalOriginal } from '@chakra-ui/react';
import { Dialog, Portal as PortalCustom } from '@halo/ui-react';
import { useRepairFocus } from '@halo/ui-react';
import { ButtonGray, ButtonRed, WarningIcon } from '@halo/ui-chat-react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

export interface ConfirmDialogProps {
  open: boolean;
  disabledPortal?: boolean;
  portalContainerRef?: RefObject<HTMLDivElement>;
  icon?: React.ReactNode;
  title: React.ReactNode;
  subTitle?: string | React.ReactNode;
  cancelText?: string;
  okText?: string;
  onClose: () => void;
  onCancel?: () => void;
  onOk?: () => void;
  children?: React.ReactNode;
  placement?: 'center' | 'top' | 'bottom';
  contentProps?: DialogContentProps;
  bodyProps?: DialogBodyProps;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  subTitleProps?: TextProps;
  titleProps?: TextProps;
  customPortal?: boolean;
}

export function ConfirmDialog({
  open,
  disabledPortal = true,
  portalContainerRef,
  icon = (
    <Box className="ConfirmDialogIcon" fontSize="2xl" color="fg.error" p="2.5">
      <WarningIcon />
    </Box>
  ),
  title,
  subTitle,
  cancelText,
  okText,
  onClose,
  onCancel,
  onOk,
  children,
  placement = 'center',
  contentProps,
  bodyProps,
  okButtonProps,
  cancelButtonProps,
  subTitleProps,
  titleProps,
  customPortal = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const { handleOpenChange } = useRepairFocus();

  const Portal = customPortal ? PortalCustom : PortalOriginal;
  const translatedCancelText = cancelText ?? t('buttons.cancel');
  const translatedOkText = okText ?? t('buttons.delete');

  return (
    <Dialog.Root
      placement={placement}
      open={open}
      onOpenChange={(e) => {
        handleOpenChange(e);
        if (!e.open) {
          onClose();
        }
      }}
    >
      <Portal disabled={disabledPortal} container={portalContainerRef}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content {...contentProps} borderRadius="2xl">
            <Flex gap="2" p="5">
              {icon}
              <Box overflow="hidden">
                <Box
                  className="ConfirmDialogTitle"
                  fontSize="md"
                  fontWeight="semibold"
                  flex="1"
                  py="2.5"
                  truncate
                  {...titleProps}
                >
                  {title}
                </Box>
                {!!subTitle && (
                  <Box
                    className="ConfirmDialogSubTitle"
                    fontSize="sm"
                    fontWeight="semibold"
                    color="fg.muted"
                    pb="5"
                    {...subTitleProps}
                  >
                    {subTitle}
                  </Box>
                )}
                <Box {...bodyProps}>{children}</Box>
              </Box>
            </Flex>
            <Dialog.CloseTrigger />
            <Dialog.Footer px="5">
              <ButtonGray
                variant="subtle"
                color="fg"
                bg="bg.subtle"
                onClick={() => {
                  onCancel ? onCancel() : onClose();
                }}
                {...cancelButtonProps}
              >
                {translatedCancelText}
              </ButtonGray>
              <ButtonRed onClick={() => onOk?.()} {...okButtonProps}>
                {translatedOkText}
              </ButtonRed>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
